import {
  ProducerMillType,
  ProducerType,
  SupplierType,
} from "@pages/shippingView/types";
import {
  autocompleteFieldConfig,
  staticAutocompleteFieldConfig,
} from "../config";

export enum CARGO_PACKAGING_FIELDS {
  CUSTOMER = "Customer",
  IMPORTER = "Importer",
  SUPPLIER_OF_GOODS = "Supplier of the Goods",
  MILL_INSTALLATION = "Mill Installation",
  MILL_OPERATOR = "Mill Operator",
  PRODUCTION_METHOD = "Production Method",
  BILL_OF_LADING = "Bill of Lading",
  VESSEL = "Vessel",
  COMMODITIES = "Commodities",
  NUMBER = "ID Number",
  THICKNESS = "Thickness",
  WIDTH = "Width",
  LENGTH = "Length",
  PIECES = "Pieces",
  NET_WEIGHT = "Net Weight",
  GROSS_WEIGHT = "Gross Weight",
  GRADE = "Grade",
  HEAT = "Heat",
  NOTE1 = "Note 1 (DDT)",
  NOTE2 = "Note 2 (DDT)",
  DDT_CUSTOMS_REFERENCE = "DDT Customer Reference",
  CUSTOMER_REFERENCE = "Customer Reference",
  CONTAINER = "Container",
}

export type CargoPackagingProps = {
  id: number;
  customer: SupplierType;
  importer: SupplierType;
  supplier: SupplierType;
  producer: ProducerType;
  producer_mill: ProducerMillType;
  production_method: { id: number; name: string };
  commodity: string;
  commodity_it: string;
  ocean_vessel: string;
  id_number: string;
  thickness: string;
  width: string;
  length: string;
  pieces: number;
  net_weight: number;
  gross_weight: number;
  grade: string;
  heat: string;
  comment_1: string;
  comment_2: string;
  ddt_customer_reference_number: string;
  customer_reference_number: string;
  mbl_number: string;
  bl_container_id: number;
  bl_commodity_id: number;
  bl_container: {
    id: number;
    number: string;
    container_type: string;
    equipment_type: string;
    pieces: number;
    extra_gauge: string;
    seal: string;
    net_weight: number;
    gross_weight: number;
    volume: number;
  };
  warehouse: { id: number; name: string };
  generation_type: string;
};

export type FcrProps = {
  fcr_bank: string;
  fcr_release_date: string;
  fcr_number: string;
  fcr_status: string;
};

export type UnloadingProps = {
  pile: string;
  rows: number;
  tiers: number;
  mark: string;
  deposit_reference: string;
  depot: string;
  claims: string;
  transport_reason: string;
  unloading_comment: string;
  consignee: { id: number; name: string };
  discharge_terminal: { id: number; name: string };
  discharge_terminal_id: number;
  warehouse: { id: number; name: string };
  warehouse_id: number;
  depositor: {
    id: number;
    name: string;
  };
  destination: {
    id: number;
    address: string;
    city: {
      id: number;
      name: string;
      province: string;
      country: string;
      post_code: string;
      title: string;
    };
    customer: {
      id: number;
      name: string;
    };
  };
};

export type DeliveryProps = {
  delivery_status: string;
  loading: {
    id: number;
    mark: string | null;
    status: string;
    created_at: string;
    transport_reason: string;
    ddt_number: number;
    ddt_created_at: string;
    delivery_info: {
      id: number;
      label: string;
      volume_weight: number;
      seq_number: number;
      delivery_truck: {
        id: number;
        delivery: {
          shipping_case_id: number;
          warehouse_shifting_id: number;
          reference_order: string;
          tc_customer: {
            id: number;
            name: string;
          };
          transport_contactor: string;
          voyage_number: number;
          id: number;
          truck_supplier: {
            id: number;
            name: string;
          };
        };
        truck_plate: string;
        seq_number: number;
        truck_kind: {
          id: number;
          service_id: number;
          name: string;
          name_it: string;
          macro_category: string;
        };
      };
    };
  };
};

export enum MACRO_CATEGORY {
  NORMAL = "Camion normale",
  CONTAINER = "Camion contenitore",
  TRAIN = "Treno",
  EXCEPTIONAL = "Camion eccezionale",
}

export type WeighingPops = {
  weighing_number: string;
  weighing_position: number;
  weighing_sector: number;
  weighing_customs_procedure: number;
  weighing_warehouse_code: number;
};

export type CustomProps = {
  supplier_invoice_number: string;
  invoice_amount: number;
  invoice_value: number;
  invoice_currency: string;
  origin_charge_value: number;
  origin_charge_currency: string;
  freight_value: number;
  freight_currency: string;
  insurance_value: number;
  insurance_currency: string;
  carriage_value: number;
  carriage_currency: string;
  hs_code: string;
  incoterm: string;
  caad_code: string;
  intro_customs_bill_draft: string;
  intro_customs_bill_status: string;
  intro_customs_bill_type: string;
  intro_mrn_code: string;
  customs_release_code: string;
  customs_release_date: string;
  customs_bill_draft: string;
  customs_bill_status: string;
  mrn_code: string;
  customs_bill_type: string;
};

export type CargoPackagingResponse = {
  items:
    | (CargoPackagingProps &
        FcrProps &
        UnloadingProps &
        WeighingPops &
        DeliveryProps &
        CustomProps)[]
    | null;
  meta: Record<string, number> | null;
  total: number;
  page: number;
  size: number;
};

export type FilterElementType<T> = T extends { element: "Textfield" }
  ? string | null
  : T extends { element: "Autocomplete"; multi: true }
    ? any[] | null
    : T extends { element: "Autocomplete"; multi: false }
      ? any | null
      : T extends { element: "Datepicker" }
        ? [string | null, string | null] | null
        : never;

export type FiltersType = {
  label?: string;
  offer_id?: number;
} & {
  [key: string]: string | string[] | number | boolean | null | undefined;
};

export type FilterValue<K extends keyof FiltersType> = FiltersType[K];

export type AutocompleteFieldKey = keyof typeof autocompleteFieldConfig;
export type StaticAutocompleteFieldKey =
  keyof typeof staticAutocompleteFieldConfig;

export type RequestPackingParams = {
  page?: number;
  size?: number;
  order_by?: string;
  is_locked_for_fcr?: boolean;
  [key: string]: any;
};
